import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Snackbar } from '@mui/material';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { useAuth } from '../Login/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { canGoBack, isHomePath } from '../../utils/navHistory';

/**
 * BackHandlerProvider — the single owner of the Android hardware back button.
 *
 * Before this existed, nothing in the app listened for `backButton`, so
 * Capacitor's default applied: pop the WebView history stack, exit when it is
 * empty. That is wrong here because most of what the user opens is React state,
 * not a route — the URL stays `/home` while they are three overlays deep, so
 * back exited the app and took the live MapKit instance with it.
 *
 * Back now resolves through one ladder, stopping at the first match:
 *
 *   1. A registered handler exists  → call the most recent one (LIFO).
 *   2. We are on an overlay route   → pop it, uncovering the Home underneath.
 *   3. We are not on Home           → pop, or fall back when deep-linked.
 *   4. Home, nothing open           → double-press to exit.
 *
 * Levels 2-4 live here. Level 1 is whatever screens registered via
 * `useBackHandler`, which is the only API any component should touch.
 *
 * On web this provider is inert: it renders children, registers no listener,
 * and every on-screen back control keeps behaving exactly as before.
 */

export const BackHandlerContext = createContext(null);

/** How long the "press again to exit" window stays open. */
const EXIT_WINDOW_MS = 2000;

const EXIT_TOAST = {
  TH: 'กดย้อนกลับอีกครั้งเพื่อออกจากแอป',
  CN: '再按一次返回键退出应用',
  EN: 'Press back again to exit',
};

export const BackHandlerProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { language } = useLanguage();

  const [exitToastOpen, setExitToastOpen] = useState(false);

  // The handler stack. A ref, not state: registering must never re-render, or
  // every overlay open would re-render the whole tree above it.
  const handlersRef = useRef([]);

  // The listener is registered once, so anything it reads must come from a ref
  // rather than a closed-over render value.
  const locationRef = useRef(location);
  const isAuthenticatedRef = useRef(isAuthenticated);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  const exitArmedRef = useRef(false);
  const exitTimerRef = useRef(null);

  const disarmExit = useCallback(() => {
    exitArmedRef.current = false;
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    setExitToastOpen(false);
  }, []);

  const register = useCallback((fn) => {
    const entry = { fn };
    handlersRef.current.push(entry);
    // Opening anything cancels a pending exit — otherwise a user who pressed
    // back once, then opened a panel, could exit on their next back press.
    exitArmedRef.current = false;
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    return () => {
      const i = handlersRef.current.indexOf(entry);
      if (i !== -1) handlersRef.current.splice(i, 1);
    };
  }, []);

  const handleBack = useCallback(() => {
    // 1. Whatever was opened most recently gets first refusal.
    const top = handlersRef.current[handlersRef.current.length - 1];
    if (top) {
      disarmExit();
      top.fn();
      return;
    }

    const current = locationRef.current;

    // 2. Overlay routes (/dashboard, /tour-guide, /login opened over Home) are
    //    rendered with `state.background`, so Home is still mounted underneath.
    //    Popping uncovers it untouched; navigating would remount it.
    if (current?.state?.background) {
      disarmExit();
      navigate(-1);
      return;
    }

    // 3. Any other route: pop when there is real history, otherwise land
    //    somewhere sensible. Cold starts and deep links have no entry behind
    //    them, and navigate(-1) is a silent no-op there — the same trap
    //    hooks/useSmartBack.js exists to avoid, resolved the same way.
    if (!isHomePath(current?.pathname)) {
      disarmExit();
      if (canGoBack()) navigate(-1);
      else navigate(isAuthenticatedRef.current ? '/dashboard' : '/home');
      return;
    }

    // 4. Home with nothing left to close.
    if (exitArmedRef.current) {
      disarmExit();
      CapacitorApp.exitApp();
      return;
    }
    exitArmedRef.current = true;
    setExitToastOpen(true);
    exitTimerRef.current = setTimeout(() => {
      exitArmedRef.current = false;
      exitTimerRef.current = null;
      setExitToastOpen(false);
    }, EXIT_WINDOW_MS);
  }, [navigate, disarmExit]);

  // Keep the listener itself stable; route the event through a ref so we never
  // tear down and re-add the native listener on navigation.
  const handleBackRef = useRef(handleBack);
  useEffect(() => {
    handleBackRef.current = handleBack;
  }, [handleBack]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    let handle;
    let cancelled = false;

    CapacitorApp.addListener('backButton', () => handleBackRef.current())
      .then((h) => {
        if (cancelled) h.remove();
        else handle = h;
      })
      .catch((err) => {
        console.error('[BackHandlerProvider] failed to attach backButton listener:', err);
      });

    return () => {
      cancelled = true;
      if (handle) handle.remove();
    };
  }, []);

  useEffect(() => () => {
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
  }, []);

  const value = useMemo(() => ({ register }), [register]);

  return (
    <BackHandlerContext.Provider value={value}>
      {children}
      <Snackbar
        open={exitToastOpen}
        message={EXIT_TOAST[language] || EXIT_TOAST.EN}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ mb: 'calc(var(--mobile-bottom-nav-height, 60px) + 12px)' }}
      />
    </BackHandlerContext.Provider>
  );
};

export default BackHandlerProvider;
